import React from "react";
import useDebouncedValue from "../useDebouncedValue";

export default function useCatalogResults({
  query,
  loadPage,
  refreshRevision = 0,
  enabled = true,
}) {
  const term = query.trim();
  const debounced = useDebouncedValue(term, 300);
  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [fetched, setFetched] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [retryRevision, setRetryRevision] = React.useState(0);
  const criteria = React.useRef({ term, loadPage, refreshRevision });
  const requestPending = React.useRef(false);

  React.useEffect(() => {
    const previous = criteria.current;
    criteria.current = { term, loadPage, refreshRevision };
    if (
      previous.term !== term ||
      previous.loadPage !== loadPage ||
      previous.refreshRevision !== refreshRevision
    ) {
      setItems([]);
      setFetched(false);
      setHasMore(false);
      setError(null);
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    if (!enabled) {
      requestPending.current = false;
      setLoading(false);
      return;
    }
    if (term !== debounced) {
      requestPending.current = true;
      setLoading(true);
      return;
    }
    const controller = new AbortController();
    requestPending.current = true;
    setLoading(true);
    setError(null);
    loadPage(page, term, controller.signal)
      .then(({ items: next, pageSize }) => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(next))
          throw new Error("Invalid collection response");
        setItems((previousItems) =>
          page === 1 ? next : [...previousItems, ...next],
        );
        setHasMore(next.length >= pageSize);
        setFetched(true);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) setError(failure);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          requestPending.current = false;
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [
    term,
    debounced,
    loadPage,
    page,
    enabled,
    refreshRevision,
    retryRevision,
  ]);

  const loadMore = React.useCallback(() => {
    if (hasMore && !loading && !requestPending.current && !error && fetched) {
      requestPending.current = true;
      setPage((value) => value + 1);
    }
  }, [hasMore, loading, error, fetched]);
  const retry = React.useCallback(
    () => setRetryRevision((value) => value + 1),
    [],
  );
  return { items, hasMore, loading, fetched, error, loadMore, retry };
}
